import React, { useMemo, useState } from 'react';
import { Box, Chip, FormHelperText, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FIELD_BORDER, PLACEHOLDER_COLOR } from './manageEventFieldStyles';

const DEFAULT_SELECTED_CHIP_SX = {
  height: 32,
  borderRadius: '999px',
  bgcolor: 'primary.light',
  color: 'text.primary',
  fontWeight: 500,
  fontSize: '0.875rem',
  '& .MuiChip-deleteIcon': {
    color: 'error.main',
    fontSize: 18,
    '&:hover': {
      color: 'error.dark',
    },
  },
};

const DEFAULT_SUGGESTION_CHIP_SX = {
  height: 34,
  borderRadius: '999px',
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'text.primary',
  color: 'text.primary',
  fontWeight: 500,
  fontSize: '0.875rem',
  cursor: 'pointer',
  '&:hover': {
    bgcolor: 'action.hover',
  },
};

const ChipInput = ({
  value = [],
  onChange,
  placeholder = 'Search',
  suggestions = [],
  suggestionsLabel = 'Suggested',
  error = false,
  helperText = '',
  disabled = false,
  onAddItem,
  onRemoveItem,
  selectedChipSx = DEFAULT_SELECTED_CHIP_SX,
  suggestionChipSx = DEFAULT_SUGGESTION_CHIP_SX,
}) => {
  const [search, setSearch] = useState('');
  const selectedItems = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const availableSuggestions = useMemo(
    () =>
      suggestions.filter(
        (item) => !selectedItems.some((selected) => selected.toLowerCase() === item.toLowerCase()),
      ),
    [selectedItems, suggestions],
  );

  const commitItem = (item) => {
    const nextItems = onAddItem(selectedItems, item);
    if (nextItems !== selectedItems) {
      onChange(nextItems);
    }
    setSearch('');
  };

  const handleSearchKeyDown = (event) => {
    if (disabled || event.key !== 'Enter') return;
    event.preventDefault();
    commitItem(search);
  };

  const handleRemoveItem = (item) => {
    if (disabled) return;
    onChange(onRemoveItem(selectedItems, item));
  };

  return (
    <Box>
      <Box
        sx={{
          border: '1px solid',
          borderColor: error ? 'error.main' : FIELD_BORDER,
          borderRadius: '10px',
          bgcolor: disabled ? 'action.hover' : 'background.paper',
          px: 1.5,
          py: 1.25,
          minHeight: 88,
          opacity: disabled ? 0.72 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          '&:focus-within': {
            borderColor: error ? 'error.main' : 'primary.main',
          },
        }}
      >
        <TextField
          fullWidth
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          variant="standard"
          InputProps={{ disableUnderline: true }}
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
              py: 0.5,
              px: 0,
            },
            '& .MuiInputBase-input::placeholder': {
              color: PLACEHOLDER_COLOR,
              opacity: 1,
            },
          }}
        />

        {selectedItems.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {selectedItems.map((item) => (
              <Chip
                key={item}
                label={item}
                onDelete={disabled ? undefined : () => handleRemoveItem(item)}
                deleteIcon={<CloseIcon />}
                sx={selectedChipSx}
              />
            ))}
          </Box>
        ) : null}
      </Box>

      {helperText ? (
        <FormHelperText error={error} sx={{ mx: 0, mt: 0.75 }}>
          {helperText}
        </FormHelperText>
      ) : null}

      {!disabled && availableSuggestions.length > 0 ? (
        <Box sx={{ mt: 1.75 }}>
          <Box
            component="span"
            sx={{
              display: 'block',
              fontSize: '0.8125rem',
              color: PLACEHOLDER_COLOR,
              mb: 1,
            }}
          >
            {suggestionsLabel}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {availableSuggestions.map((item) => (
              <Chip
                key={item}
                label={item}
                onClick={() => commitItem(item)}
                sx={suggestionChipSx}
              />
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default ChipInput;
